import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { apiGet } from '../utils/api';
import { useVatMode } from '../hooks/useVatMode';
import { useDebouncedValue, useLatestRequest } from '../hooks/useSearchRequest';
import LoadingSpinner from '../components/LoadingSpinner';
import ErrorMessage from '../components/ErrorMessage';
import Pagination, { getStoredPageSize } from '../components/Pagination';
import EmptyState from '../components/EmptyState';
import HireForgeEarlyNotice from '../components/HireForgeEarlyNotice';
import { BTN, BTN_XS } from '../constants/buttons';

// ---------------------------------------------------------------------------
// localStorage helpers
// ---------------------------------------------------------------------------
const STORAGE_KEYS = {
  category: 'sfx_marketplace_category',
  location: 'sfx_marketplace_location',
};

function loadStored(key, fallback = '') {
  try {
    return localStorage.getItem(key) || fallback;
  } catch {
    return fallback;
  }
}

function saveStored(key, value) {
  try {
    localStorage.setItem(key, value);
  } catch {
    // ignore
  }
}

// ---------------------------------------------------------------------------
// Mobile detection
// ---------------------------------------------------------------------------
function useIsMobile() {
  const [isMobile, setIsMobile] = useState(
    () => typeof window !== 'undefined' && window.matchMedia('(max-width: 767px)').matches
  );
  useEffect(() => {
    const mql = window.matchMedia('(max-width: 767px)');
    const handler = (e) => setIsMobile(e.matches);
    mql.addEventListener('change', handler);
    return () => mql.removeEventListener('change', handler);
  }, []);
  return isMobile;
}

// ---------------------------------------------------------------------------
// Camera placeholder icon (inline SVG)
// ---------------------------------------------------------------------------
function CameraPlaceholder() {
  return (
    <div className="aspect-[4/3] w-full bg-gray-100 dark:bg-gray-700 flex items-center justify-center">
      <svg
        className="h-12 w-12 text-gray-400 dark:text-gray-500"
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
        strokeWidth={1.5}
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          d="M6.827 6.175A2.31 2.31 0 015.186 7.23c-.38.054-.757.112-1.134.175C2.999 7.58 2.25 8.507 2.25 9.574V18a2.25 2.25 0 002.25 2.25h15A2.25 2.25 0 0021.75 18V9.574c0-1.067-.75-1.994-1.802-2.169a47.865 47.865 0 00-1.134-.175 2.31 2.31 0 01-1.64-1.055l-.822-1.316a2.192 2.192 0 00-1.736-1.039 48.774 48.774 0 00-5.232 0 2.192 2.192 0 00-1.736 1.039l-.821 1.316z"
        />
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          d="M16.5 12.75a4.5 4.5 0 11-9 0 4.5 4.5 0 019 0z"
        />
      </svg>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Pin icon for location
// ---------------------------------------------------------------------------
function PinIcon() {
  return (
    <svg className="h-3.5 w-3.5 inline-block mr-0.5 text-gray-400 dark:text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M15 10.5a3 3 0 11-6 0 3 3 0 016 0z" />
      <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 10.5c0 7.142-7.5 11.25-7.5 11.25S4.5 17.642 4.5 10.5a7.5 7.5 0 1115 0z" />
    </svg>
  );
}

// ---------------------------------------------------------------------------
// Listing card
// ---------------------------------------------------------------------------
// Exported for MarketplaceBrowse.test.js — the card only renders when the
// marketplace has listings, a path production data exercises but an empty
// dev DB never did (the formatMoney ReferenceError shipped that way).
export function ListingCard({ listing }) {
  const { formatMoney } = useVatMode();
  const primaryImage = listing.primary_image;

  return (
    <Link
      to={`/hireforge/listings/${listing.id}`}
      className="block bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden shadow-sm hover:shadow-md transition-shadow"
    >
      {/* Image */}
      {primaryImage ? (
        <img
          src={primaryImage}
          alt={listing.title}
          className="aspect-[4/3] w-full object-cover bg-gray-100 dark:bg-gray-700"
          loading="lazy"
        />
      ) : (
        <CameraPlaceholder />
      )}

      {/* Body */}
      <div className="p-4 space-y-1.5">
        {/* Title */}
        <h3
          className="text-lg font-semibold text-gray-900 dark:text-white truncate"
          title={listing.title}
        >
          {listing.title}
        </h3>

        {/* Category pill */}
        {listing.category_name && (
          <span className="inline-block bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300 text-xs px-2 py-0.5 rounded-full">
            {listing.category_name}
          </span>
        )}

        {/* Location */}
        {listing.location && (
          <p className="text-sm text-gray-500 dark:text-gray-400">
            <PinIcon />
            {listing.location}
          </p>
        )}

        {/* Price */}
        <div className="text-sm font-medium text-gray-900 dark:text-white">
          {listing.is_poa ? (
            <span className="inline-block bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300 text-xs px-2 py-0.5 rounded-full font-semibold">
              POA
            </span>
          ) : (
            <span>
              {listing.daily_rate != null && (
                <>{formatMoney(listing.daily_rate)}/day</>
              )}
              {listing.daily_rate != null && listing.weekly_rate != null && (
                <span className="text-gray-400 dark:text-gray-500"> &middot; </span>
              )}
              {listing.weekly_rate != null && (
                <>{formatMoney(listing.weekly_rate)}/wk</>
              )}
            </span>
          )}
        </div>

        {/* Poster */}
        {(listing.contact_name || listing.poster_name) && (
          <p className="text-sm text-gray-500 dark:text-gray-400">
            {listing.contact_name || listing.poster_name}
          </p>
        )}
      </div>
    </Link>
  );
}

// ---------------------------------------------------------------------------
// Main page component
// ---------------------------------------------------------------------------
export default function MarketplaceBrowse() {
  const navigate = useNavigate();
  const isMobile = useIsMobile();

  // --- State ---
  const [categories, setCategories] = useState([]);
  const [listings, setListings] = useState([]);
  const [totalItems, setTotalItems] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [search, setSearch] = useState('');
  const [selectedCategory, setSelectedCategory] = useState(() => loadStored(STORAGE_KEYS.category, ''));
  const [locationFilter, setLocationFilter] = useState(() => loadStored(STORAGE_KEYS.location, ''));

  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(getStoredPageSize);

  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);

  // Effective page size for mobile
  const effectivePageSize = isMobile ? 50 : pageSize;

  // --- Debounce search (shared hook — see hooks/useSearchRequest.js) ---
  const debouncedSearch = useDebouncedValue(search);
  const beginRequest = useLatestRequest();

  // A new search term must return to page 1: searching while on page 3 of the
  // old results otherwise lands on a page the new result set does not have,
  // which renders as an empty list and reads as "no results".
  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearch]);

  // --- Persist filters ---
  useEffect(() => {
    saveStored(STORAGE_KEYS.category, selectedCategory);
  }, [selectedCategory]);

  useEffect(() => {
    saveStored(STORAGE_KEYS.location, locationFilter);
  }, [locationFilter]);

  // --- Fetch categories ---
  useEffect(() => {
    apiGet('/api/marketplace/categories')
      .then((res) => { if (!res.ok) throw new Error('Failed'); return res.json(); })
      .then((data) => setCategories(Array.isArray(data) ? data : []))
      .catch(() => setCategories([]));
  }, []);

  // --- Fetch listings ---
  const fetchListings = useCallback(async () => {
    const isCurrent = beginRequest();
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      if (debouncedSearch) params.set('q', debouncedSearch);
      if (selectedCategory) params.set('category', selectedCategory);
      if (locationFilter) params.set('location', locationFilter);

      const limit = effectivePageSize === 'ALL' ? '' : String(effectivePageSize);
      if (limit) {
        params.set('limit', limit);
        params.set('page', String(currentPage));
      }

      const qs = params.toString();
      const res = await apiGet(`/api/marketplace/listings${qs ? '?' + qs : ''}`);
      if (!res.ok) throw new Error('Failed to fetch listings');
      const data = await res.json();
      // Stale-response guard — a slower earlier query must not overwrite the
      // results for what is in the box now. See hooks/useSearchRequest.js.
      if (!isCurrent()) return;

      if (Array.isArray(data)) {
        setListings(data);
        setTotalItems(data.length);
      } else if (data && Array.isArray(data.listings)) {
        setListings(data.listings);
        setTotalItems(data.total ?? data.listings.length);
      } else {
        setListings([]);
        setTotalItems(0);
      }
    } catch (err) {
      // If the API isn't available yet, treat as empty rather than error
      if (!isCurrent()) return;
      setError(null);
      setListings([]);
      setTotalItems(0);
    } finally {
      if (isCurrent()) setLoading(false);
    }
  }, [debouncedSearch, selectedCategory, locationFilter, currentPage, effectivePageSize, beginRequest]);

  useEffect(() => {
    fetchListings();
  }, [fetchListings]);

  // --- Active filter count (for mobile badge) ---
  const activeFilterCount =
    (selectedCategory ? 1 : 0) + (locationFilter ? 1 : 0);

  // --- Handlers ---
  const handleCategoryChange = (cat) => {
    setSelectedCategory(cat);
    setCurrentPage(1);
  };

  const handleLocationChange = (e) => {
    setLocationFilter(e.target.value);
    setCurrentPage(1);
  };

  const handlePageChange = (page) => setCurrentPage(page);

  const handlePageSizeChange = (size) => {
    setPageSize(size);
    setCurrentPage(1);
  };

  // --- Render ---
  return (
    <div className="max-w-7xl mx-auto px-4 py-6 space-y-6">
      {/* Early-version notice — see components/HireForgeEarlyNotice.js for the
          copy, the not-dismissible decision, and why it is on this page and
          not on the listing detail. No margin class: the container's
          `space-y-6` already spaces it, and a second margin utility here would
          resolve against that by stylesheet order rather than by intent. */}
      <HireForgeEarlyNotice />

      {/* Header */}
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-3">
          Hire Forge
        </h1>
        <button
          className={BTN.primary}
          onClick={() => navigate('/hireforge/listings/new')}
        >
          Add Listing
        </button>
      </div>

      {/* Search bar */}
      <div>
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search equipment..."
          className="w-full min-h-[44px] md:min-h-0 text-base px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:focus:ring-blue-400 dark:focus:border-blue-400 outline-none transition-colors"
        />
      </div>

      {/* Mobile filter toggle (md:hidden) */}
      <div className="md:hidden">
        <button
          className={BTN.secondary}
          onClick={() => setMobileFiltersOpen((v) => !v)}
          aria-expanded={mobileFiltersOpen}
        >
          Filters
          {activeFilterCount > 0 && (
            <span className="ml-1.5 inline-flex items-center justify-center h-5 w-5 rounded-full bg-blue-600 dark:bg-blue-500/70 text-white text-xs font-bold">
              {activeFilterCount}
            </span>
          )}
        </button>
      </div>

      {/* Filters */}
      <div
        className={`${
          mobileFiltersOpen ? 'flex' : 'hidden'
        } md:flex flex-col md:flex-row md:items-center gap-4`}
      >
        {/* Category pills */}
        <div className="flex flex-wrap gap-2">
          <button
            onClick={() => handleCategoryChange('')}
            className={`px-3 py-1 text-sm rounded-full font-medium transition-colors ${
              selectedCategory === ''
                ? 'bg-blue-600 text-white dark:bg-blue-500/70 dark:text-white'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600'
            }`}
          >
            All
          </button>
          {categories.map((cat) => (
            <button
              key={cat.id || cat.name}
              onClick={() => handleCategoryChange(String(cat.id || cat.name))}
              className={`px-3 py-1 text-sm rounded-full font-medium transition-colors ${
                selectedCategory === String(cat.id || cat.name)
                  ? 'bg-blue-600 text-white dark:bg-blue-500/70 dark:text-white'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600'
              }`}
            >
              {cat.name}
              {cat.count != null && (
                <span className="ml-1 opacity-70">({cat.count})</span>
              )}
            </button>
          ))}
        </div>

        {/* Location filter */}
        <div className="flex-shrink-0 w-full md:w-56">
          <input
            type="text"
            value={locationFilter}
            onChange={handleLocationChange}
            placeholder="Filter by location..."
            className="w-full min-h-[44px] md:min-h-0 text-base px-3 py-1.5 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:focus:ring-blue-400 dark:focus:border-blue-400 outline-none transition-colors text-sm"
          />
        </div>
      </div>

      {/* Error */}
      {error && <ErrorMessage error={error} />}

      {/* Loading */}
      {loading && <LoadingSpinner message="Loading listings..." />}

      {/* Results grid */}
      {!loading && !error && listings.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {listings.map((listing) => (
            <ListingCard key={listing.id} listing={listing} />
          ))}
        </div>
      )}

      {/* Empty state */}
      {!loading && !error && listings.length === 0 && (
        <EmptyState
          icon={
            <svg className="mx-auto h-12 w-12 text-gray-300 dark:text-gray-600 mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 15.803a7.5 7.5 0 0010.607 0z" />
            </svg>
          }
          title={debouncedSearch || selectedCategory || locationFilter ? 'No matching listings' : 'No listings yet'}
          description={
            debouncedSearch || selectedCategory || locationFilter
              ? 'Try adjusting your search or filters to find what you\'re looking for.'
              : 'New equipment listings from other productions will appear here. Be the first to list something.'
          }
        />
      )}

      {/* Pagination */}
      {!loading && listings.length > 0 && (
        <Pagination
          totalItems={totalItems}
          currentPage={currentPage}
          pageSize={effectivePageSize}
          onPageChange={handlePageChange}
          onPageSizeChange={handlePageSizeChange}
        />
      )}
    </div>
  );
}
